import { type HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import type { QueryClient } from '@tanstack/react-query';

import type { History } from 'history';

import { getAccessToken } from 'api/authentication/utils/getAccessToken';
import type { IDataEventBase, UpdateProjectModel } from 'api/data/client-dataapi';
import uniq from 'lodash/uniq';
import { getProposalContextFromPathname } from 'state/grids/utils/shouldProcessEvent';
import { reconcileImportsAfterReconnect } from 'state/imports/importsInProgress';
import { PENDING_EDITS_STATE } from 'state/pendingEdits/pendingEditsState';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';
import type { SemNode } from 'types/data/semNode';
import { devLog } from 'utils/dev-utils';
import { hasValue } from 'utils/values';

import { getResultSetEntry } from '../grids/getResultSetEntry';
import { GRID_STORE, flattenSemNode } from '../grids/gridState';
import { PROJECTS_STORE } from '../projects/projectsStore';
import { type HubConnectionManagerConfig, createHubConnectionManager } from './hubConnectionManager';
import type {
  PartialRefreshDataRequest,
  PropertySelectedEvent,
  QueryRequestModel,
  RootRowsRequestedResult,
  RootRowsResult
} from './signalrState';

const signalRHubConnectionManager = createHubConnectionManager();

export class SignalRActions {
  private subscriberId: string;
  private secureUrl: string;

  private userEmail: string;
  private history: History;
  private queryClient: QueryClient;

  constructor(subscriberId: string, userEmail: string, history: History, secureUrl: string, queryClient: QueryClient) {
    this.subscriberId = subscriberId;
    this.userEmail = userEmail;
    this.history = history;
    this.secureUrl = secureUrl;
    this.queryClient = queryClient;
  }

  private hubConfig(): HubConnectionManagerConfig {
    const { showSnackbar } = SNACKBAR_STORE.getState();

    return {
      buildConnection: () =>
        new HubConnectionBuilder()
          .withUrl(this.secureUrl, { accessTokenFactory: () => getAccessToken() })
          .withHubProtocol(new MessagePackHubProtocol())
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Critical)
          .build(),
      bindEvents: (connection) => this.BindEvents(connection),
      onUnexpectedClose: (error) => {
        if (hasValue(error)) {
          // If no error is provided, the connection was manually closed by calling `connection.stop()`.
          // Only log when the connection got shut down unexpectedly.
          devLog('SignalR connection closed unexpectedly:', String(error));
        }
      },
      // Only show a message if it didn't succeed on the first try.
      onConnecting: (isRetry) => {
        if (isRetry) {
          showSnackbar('Connecting...', 'info', false);
        }
      },
      onConnected: (isRetry) => {
        if (isRetry) {
          showSnackbar('Connected', 'success', true);
        }
      },
      onConnectFailed: (retryInSeconds) => {
        showSnackbar(`Failed to connect. Retry in ${retryInSeconds} seconds.`, 'warning', false);
      },
      onReconnecting: () => {
        showSnackbar('Reconnecting...', 'info', false);
      },
      onReconnected: () => {
        showSnackbar('Connected', 'success', true);
        reconcileImportsAfterReconnect(this.queryClient);
      },
      // `withAutomaticReconnect()` only covers a connection that was established at least once and
      // gives up after its own retry policy is exhausted - this is what still brings it back after that.
      onRestarting: () => {
        showSnackbar('Disconnected. Restarting...', 'info', false);
      },
      onRestarted: () => {
        showSnackbar('Connected.', 'success', true);
        reconcileImportsAfterReconnect(this.queryClient);
      },
      onRestartFailed: (error) => {
        devLog('Failed to restart connection', String(error));
        // The only way to recover here is to refresh the page.
        showSnackbar('Failed to restart connection. Please refresh the page to try again.', 'error', false);
      }
    };
  }

  public async on(eventName: string, callback: (...args: any[]) => void): Promise<void> {
    return signalRHubConnectionManager.on(this.subscriberId, this.hubConfig(), eventName, callback);
  }

  public async off(eventName: string, callback: (...args: any[]) => void): Promise<void> {
    return signalRHubConnectionManager.off(this.subscriberId, this.hubConfig(), eventName, callback);
  }

  async Connect(retryInSeconds = 3): Promise<HubConnection> {
    return signalRHubConnectionManager.connect(this.subscriberId, { ...this.hubConfig(), retryInSeconds });
  }

  async Disconnect() {
    return signalRHubConnectionManager.disconnect(this.subscriberId, {
      onDisconnectError: (e) => console.error(`Error while disconnecting SignalR: ${e}`)
    });
  }

  async Stream<T>(methodName: string, model: unknown) {
    const connection = await this.Connect();
    return connection.stream<T>(methodName, model);
  }

  private async invoke<T>(methodName: string, model: T) {
    const signalRHubConnection = await this.Connect();
    signalRHubConnection.invoke(methodName, model);
  }

  public RequestRootRows(models: QueryRequestModel<PartialRefreshDataRequest>[]) {
    return this.invoke('RequestRootRows', models);
  }

  public MovePropertySelection(model: PropertySelectedEvent) {
    return this.invoke('MovePropertySelection', model);
  }

  public UpdateProject(model: UpdateProjectModel) {
    return this.invoke('UpdateProject', model);
  }

  public DeleteProject(projectId: string) {
    return this.invoke('DeleteProject', { ProjectId: projectId });
  }

  private BindEvents(hubConnect: HubConnection) {
    // Connection lifecycle (onclose/restart, onreconnecting/onreconnected) is handled by
    // hubConnectionManager - see hubConfig() above for the snackbar hooks wired into it.

    hubConnect.on('ReceiveConnectionId', (args) => {
      devLog('ConnectionId received', ...args);
    });

    hubConnect.on('ProjectUpdating', (event: IDataEventBase) => {
      // a project update is initalized or
      // a project is being deleted
      PROJECTS_STORE.getState().addBlockingOperation(event.ProjectId, event.EventId, 'Updating project');
    });

    // ImportStarted, ImportCompleted, ProjectUpdateProgress, ProjectUpdated, ProjectDeleted and
    // NodePropertiesUpdated all moved to NotificationApiActions (NotificationApi's /notifications
    // hub) - DataHubContext now publishes them through NotificationApi instead of broadcasting them
    // directly off this hub. See that file's BindEvents for the current handlers.

    // SelectionMoved moved to NotificationApiActions (NotificationApi's /notifications hub) -
    // see that file. DataApi's DataHub still broadcasts it too (backend migration deferred),
    // but the frontend no longer listens for it on this connection to avoid double-handling.

    hubConnect.on('PropertySelectionMoved', (event: PropertySelectedEvent) => {
      if (GRID_STORE.getState().projectId !== event.ProjectId) {
        return;
      }

      if (this.userEmail !== event.User) {
        GRID_STORE.getState().editNodeProperty(event.User, event.NodeId, event.PropertyName);
      }
    });

    hubConnect.on('RootRowsUpdated', async (event: RootRowsRequestedResult) => {
      if (GRID_STORE.getState().projectId !== event.ProjectId) {
        return;
      }

      if (!event.Results?.length) {
        return;
      }
      const { proposalId: eventProposalId } = getResultSetEntry(event.Results?.[0]?.ResultSetId ?? '');
      const { currentProposalId } = getProposalContextFromPathname(this.history.location.pathname);
      if (eventProposalId !== currentProposalId) {
        return;
      }

      const changes = [] as string[];
      const gridStoreState = GRID_STORE.getState();

      // the event contains the new root row
      // store the nodes in the property store
      const semNodes = event.Results.flatMap((r) =>
        r.QueryResults.flatMap((q) => q.Results.Result.flatMap(flattenSemNode))
      );

      // merge nodes together
      const distinctSemNodeDictionary = semNodes.reduce(
        (prev, curr) => {
          const node = prev[curr.Id];

          if (!node) {
            prev[curr.Id] = curr;

            return prev;
          }

          Object.keys(curr.Properties).forEach((prop) => {
            let property = node.Properties[prop];
            if (property) {
              return;
            }
            property = curr.Properties[prop];
          });

          return prev;
        },
        {} as { [id: string]: SemNode }
      );

      gridStoreState.addNodeProperties(Object.values(distinctSemNodeDictionary));

      event.Results.forEach((rootRowResult: RootRowsResult) => {
        rootRowResult.QueryResults.forEach((rootRowResultData) => {
          if (rootRowResultData.Results.Success) {
            const { queryId, dataNodeId } = getResultSetEntry(rootRowResult.ResultSetId);
            if (dataNodeId !== undefined && queryId === rootRowResultData.QueryId) {
              // the result is the exact data node result
              gridStoreState.replaceRootNodesWithoutRecalculation(
                rootRowResult.ResultSetId,
                rootRowResultData.Results.Result
              );
              changes.push(rootRowResult.ResultSetId);
            } else {
              const nodesToRemove = [] as string[];
              const nodesToUpdate = [] as SemNode[];
              rootRowResultData.RequestedNodeIds.forEach((requestedNodeId) => {
                const updatedNode = rootRowResultData.Results.Result.find((n) => n.Id === requestedNodeId);

                if (updatedNode !== undefined) {
                  nodesToUpdate.push(updatedNode);
                  changes.push(rootRowResult.ResultSetId);
                } else {
                  nodesToRemove.push(requestedNodeId);
                  changes.push(rootRowResult.ResultSetId);
                }
              });

              if (nodesToRemove.length > 0) {
                gridStoreState.removeRootNodesWithoutRecalculation(rootRowResult.ResultSetId, nodesToRemove);
              }

              if (nodesToUpdate.length > 0) {
                const updatedNodes = nodesToUpdate.flatMap(flattenSemNode);
                const uniqueNodesToUpdate = uniq(updatedNodes);

                gridStoreState.addNodeProperties(uniqueNodesToUpdate);
                gridStoreState.updateRootNodesWithoutRecalculation(rootRowResult.ResultSetId, nodesToUpdate);
              }
            }
          } else {
            // console.warn(event.Result.Error);
          }
        });
      });

      PENDING_EDITS_STATE.getState().addPendingEditToClearOnNextRender(event.PendingEventId);

      if (changes.length > 0) {
        const resultSetsToUpdate = uniq(changes);
        await gridStoreState.recalculateResultSets(resultSetsToUpdate, [event.PendingEventId]);
      }
    });

    // RelationshipsChanged, HyperRelationshipDeletionBlocked, NodeMovedInDecomposition and RootRowAdded
    // all moved to NotificationApiActions (NotificationApi's /notifications hub) - DataHubContext
    // now publishes them through NotificationApi instead of broadcasting them directly off this hub.
    // 'DataNodeDeleted' moved to NotificationApiActions - DataHubContext now publishes it through
    // NotificationApi instead of broadcasting it directly off this hub. See NotificationApiActions.BindEvents.

    // ProjectTeamMappingDeleted, OwnershipMappingUpdated, TemplateBranchCreated, TemplateRemoved,
    // TemplateReleaseCompleted, ReportRunUpdated, DataNodeCreated, DataRelationCreated,
    // DataRelationDeleted and DataNodeUpdated all moved to NotificationApiActions (NotificationApi's
    // /notifications hub) - DataHubContext now publishes them through NotificationApi instead of
    // broadcasting them directly off this hub. See that file's BindEvents for the current handlers.
  }
}
